// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import Widget from '@arcgis/core/widgets/Widget';
import { tsx } from '@arcgis/core/widgets/support/widget';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery';
import styles from './BasemapStyles';

function styleObjToString(styleObj: Record<string, string | number>): string {
  return Object.entries(styleObj)
    .map(([key, value]) => {
      const kebabKey = key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
      return `${kebabKey}:${value}`;
    })
    .join(';');
}

interface BasemapProps {
  view: MapView | SceneView;
}

class BasemapWidget extends Widget {
  view: MapView | SceneView;

  basemapGallery: BasemapGallery | null = null;

  isActive = false;

  isBasemapVisible = true;

  originalGround: any;

  name = 'basemapWidget';

  constructor(params: BasemapProps) {
    super(params);
    this.view = params.view;
  }

  // Open / close panel
  _handleClick = (): void => {
    this.isActive = !this.isActive;
    this.scheduleRender();
    if (this.isActive === false) {
      this.emit('basemap-toggle-close', { active: this.isActive });
    } else {
      this.emit('basemap-toggle', { active: this.isActive });
    }
  };

  _toggleBasemapVisibility = (): void => {
    const { map } = this.view;
    const nextVisible = !this.isBasemapVisible;

    // Store the ground only once
    if (!this.originalGround) {
      this.originalGround = map.ground;
    }

    map.ground.opacity = nextVisible ? 1 : 0;

    // A basemap can carry more than one layer - base imagery/vector tiles plus reference
    // labels, boundaries, or (as with the 3D Topographic style) building footprints - hiding
    // only the ground left every one of them still drawn regardless of the eye icon.
    if (map.basemap) {
      map.basemap.baseLayers.forEach((layer) => {
        layer.visible = nextVisible;
      });
      map.basemap.referenceLayers.forEach((layer) => {
        layer.visible = nextVisible;
      });
    }

    // Switching basemaps while hidden would swap in a fresh Basemap whose own layers default
    // to visible, breaking the toggle - keep the gallery disabled for as long as it's hidden.
    if (this.basemapGallery) {
      this.basemapGallery.disabled = !nextVisible;
    }

    this.isBasemapVisible = nextVisible;
    this.scheduleRender();
  };

  render() {
    return tsx(
      'div',
      { style: styleObjToString(styles.container) },

      // Main toggle button
      tsx(
        'div',
        {
          class: 'esri-widget esri-widget--button',
          onclick: this._handleClick.bind(this),
          style: 'cursor:pointer;',
          title: 'Basemap Gallery',
        },
        tsx('calcite-icon', {
          icon: this.isActive ? 'x' : 'basemap',
          scale: 'm',
        }),
      ),

      // Panel (visible when active)
      tsx(
        'div',
        {
          style: styleObjToString(styles.panel(this.isActive)),
        },

        // Header + visibility toggle (eye icon)
        tsx(
          'div',
          { style: styleObjToString(styles.header) },

          tsx(
            'span',
            { style: styleObjToString(styles.headerTitle) },
            'Basemap Gallery',
          ),

          tsx(
            'div',
            {
              onclick: this._toggleBasemapVisibility,
              style: styleObjToString(styles.eyeToggle),
              title: this.isBasemapVisible ? 'Hide basemap' : 'Show basemap',
            },
            tsx('calcite-icon', {
              icon: this.isBasemapVisible ? 'view-visible' : 'view-hide',
              scale: 'm',
            }),
          ),
        ),

        // Basemap Gallery container
        tsx('div', {
          afterCreate: (el: HTMLDivElement) => {
            if (!this.basemapGallery) {
              this.basemapGallery = new BasemapGallery({
                view: this.view,
                container: el,
              });

              // Make sure initial state is correct
              if (!this.isBasemapVisible) {
                this.basemapGallery.disabled = true;
              }
            }
          },
          style: styleObjToString(styles.basemapDiv),
        }),
      ),
    );
  }
}

export default BasemapWidget;
